/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import _ from 'lodash';
import { fromObjectEntries, objectEntries } from '../utils';
const transpose = (data) => data.length > 0 && Array.isArray(data[0]) ? data[0].map((_, i) => data.map((row) => row[i])) : [];
const parseCsvString = (s, skipCol = 0) => {
    const matrix = s.split('\n').map((row) => row.split(','));
    const transposed = transpose(matrix.map((row) => row.slice(skipCol, row.length)));
    return fromObjectEntries(transposed.map((col) => [col[0], col.slice(1).filter((x) => Boolean(x))]));
};
const parseTsCol = (vals) => vals.map(Number).filter((x) => !Number.isNaN(x));
const parse12x24 = (vals) => vals.map((row) => row.slice(2, row.length).map(Number));
const parseMatrix = (rawStr) => rawStr.split('\n').map((row) => row.split(','));
const parseIntoObj = (key, entries) => {
    let ts = {};
    if (entries.length > 0) {
        ts = {
            [key]: fromObjectEntries(entries.map(([s, vals]) => [
                s.split('.')?.[1] ? s.split('.')[1] : s,
                typeof vals[0] === 'string'
                    ? parseTsCol(vals)
                    : parse12x24(vals),
            ])),
        };
    }
    return ts;
};
const parseIntoNestedObj = (key, entries) => {
    let ts = {};
    if (entries.length > 0) {
        const uniques = _.uniq(entries.map(([s]) => s.split('.')[0]));
        ts = {
            [key]: fromObjectEntries(uniques.map((component) => {
                const filteredEntries = entries.filter(([s]) => s.includes(component));
                return [
                    component,
                    fromObjectEntries(filteredEntries.map(([s, vals]) => [
                        s.split('.')[1],
                        typeof vals[0] === 'string'
                            ? parseTsCol(vals)
                            : parse12x24(vals),
                    ])),
                ];
            })),
        };
    }
    return ts;
};
const parseResults = (entries, timestamp) => {
    const bessEntries = entries.filter(([key]) => /bess-\d/.test(key));
    const h2Entries = entries.filter(([key]) => key.includes('h2.'));
    const thermalEntries = entries.filter(([key]) => /thermal-\d/.test(key));
    const busEntries = entries.filter(([key]) => key.includes('bus.') || key.includes('poi.'));
    const priceDispatchEntries = entries.filter(([key]) => key.includes('price_dispatch.'));
    const ptcEntries = entries.filter(([key]) => key.includes('ptc.'));
    const recEntries = entries.filter(([key]) => key.includes('rec.'));
    const shapeEntries = entries.filter(([key]) => key.includes('shape.'));
    const pvEntries = entries.filter(([key]) => key.includes('pv.'));
    const windEntries = entries.filter(([key]) => key.includes('wind.'));
    const totalRevenueEntries = entries.filter(([key]) => key === 'total_revenue');
    const totalCostEntries = entries.filter(([key]) => key === 'total_cost');
    const totalNetCostEntries = entries.filter(([key]) => key === 'total_net_cost');
    const totalMVBusToPoiEffEntries = entries.filter(([key]) => key === 'mv_bus_to_poi_eff');
    const totalGenAtPOIEntries = entries.filter(([key]) => key === 'total_gen_at_poi');
    const totalGenAtLoadEntries = entries.filter(([key]) => key === 'total_gen_at_load');
    const bess = parseIntoNestedObj('bess', bessEntries);
    const h2 = parseIntoObj('h2', h2Entries);
    const thermal = parseIntoNestedObj('thermal', thermalEntries);
    const bus = parseIntoNestedObj('bus', busEntries);
    const priceDispatch = parseIntoObj('price_dispatch', priceDispatchEntries);
    const ptc = parseIntoObj('ptc', ptcEntries);
    const rec = parseIntoObj('rec', recEntries);
    const shape = parseIntoObj('shape', shapeEntries);
    const pv = parseIntoObj('pv', pvEntries);
    const wind = parseIntoObj('wind', windEntries);
    const totalRevenueObj = parseIntoObj('total_revenue', totalRevenueEntries);
    const totalCostObj = parseIntoObj('total_cost', totalCostEntries);
    const totalNetCostObj = parseIntoObj('total_net_cost', totalNetCostEntries);
    const totalMVBusToPoiEffObj = parseIntoObj('mv_bus_to_poi_eff', totalMVBusToPoiEffEntries);
    const totalGenAtPOIObj = parseIntoObj('total_gen_at_poi', totalGenAtPOIEntries);
    const totalGenAtLoadObj = parseIntoObj('total_gen_at_load', totalGenAtLoadEntries);
    return {
        timestamp,
        ...bess,
        ...h2,
        ...thermal,
        ...bus,
        ...shape,
        ...priceDispatch,
        ...ptc,
        ...rec,
        ...pv,
        ...wind,
        ...totalRevenueObj,
        ...totalCostObj,
        ...totalNetCostObj,
        ...totalMVBusToPoiEffObj,
        ...totalGenAtPOIObj,
        ...totalGenAtLoadObj,
    };
};
const busFields = [
    'power_in_limit',
    'power_out_limit',
    'total_power_in',
    'total_power_out',
    'charge_eff',
    'discharge_eff',
    'power_in',
    'power_out',
    'plant_out',
    'clipped',
];
const busFieldSet = new Set(busFields);
const emptyMvBus = () => ({ bus: {} });
const isAlphaNumeric = (char) => {
    const code = char.charCodeAt(0);
    return (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
};
const normalizeResultIdentifier = (value) => [...value].filter(isAlphaNumeric).join('').toLowerCase();
const assetGroups = ['pv', 'bess', 'as_available', 'thermal'];
const addAliases = (map, aliases, key) => {
    aliases.forEach((alias) => {
        if (alias)
            map.set(normalizeResultIdentifier(alias), key);
    });
};
const addAssetAliases = (map, mvBusKey, asset, group, assetIndex) => {
    const key = `${group === 'as_available' ? 'as_available' : group}-${assetIndex + 1}`;
    [asset.name, asset.label].forEach((alias) => {
        if (alias)
            map.set(`${mvBusKey}:${normalizeResultIdentifier(alias)}`, { group, key });
    });
};
const buildRequestKeyMaps = (request) => {
    if (!request?.mv_bus?.length)
        return null;
    const mvBusKeys = new Map();
    const assetKeys = new Map();
    request.mv_bus.forEach((mvBus, mvBusIndex) => {
        const mvBusKey = `mv_bus-${mvBusIndex + 1}`;
        addAliases(mvBusKeys, [mvBus.name, mvBus.label], mvBusKey);
        assetGroups.forEach((group) => {
            mvBus[group]?.forEach((asset, assetIndex) => {
                addAssetAliases(assetKeys, mvBusKey, asset, group, assetIndex);
            });
        });
    });
    return { mvBusKeys, assetKeys };
};
const mapBusField = (field) => ({
    total_power_in: ['total_power_in', 'power_in'],
    total_power_out: ['total_power_out', 'power_out'],
    power_in_limit: ['power_in_limit'],
    power_out_limit: ['power_out_limit'],
    charge_eff: ['charge_eff'],
    discharge_eff: ['discharge_eff'],
    plant_out: ['plant_out'],
    clipped: ['clipped'],
    power_in: ['power_in'],
    power_out: ['power_out'],
})[field] ?? [];
const mapGenField = (field) => (field === 'clipped' ? 'mvbus_clipped' : field);
const getOrCreateRecord = (obj) => obj ?? {};
const assignFields = (obj, fields, value) => {
    fields.forEach((field) => {
        obj[field] = value;
    });
};
const getOrCreateMvBusAsset = (result, mvBusKey, group, assetKey) => {
    result.mv_bus = getOrCreateRecord(result.mv_bus);
    result.mv_bus[mvBusKey] = result.mv_bus[mvBusKey] ?? emptyMvBus();
    const existing = result.mv_bus[mvBusKey][group];
    result.mv_bus[mvBusKey][group] = getOrCreateRecord(existing);
    return result.mv_bus[mvBusKey][group]?.[assetKey] ?? {};
};
const assignScalarMetric = (result, key, value) => {
    if (key === 'total_revenue' || key === 'total_cost' || key === 'total_net_cost') {
        result[key] = { [key]: value };
        return true;
    }
    if (key === 'total_gen_at_poi' || key === 'total_gen_at_load') {
        result[key] = { [key]: value };
        return true;
    }
    if (key !== 'aux.power_in')
        return false;
    result.aux = { power_in: value };
    return true;
};
const assignOperationMetric = (result, key, value) => {
    const operations = ['price_dispatch', 'shape', 'ptc', 'rec'];
    const prefix = operations.find((item) => key.startsWith(`${item}.`));
    if (!prefix)
        return false;
    const metric = key.replace(`${prefix}.`, '');
    result[prefix] = getOrCreateRecord(result[prefix]);
    result[prefix][metric] = value;
    return true;
};
const assignBusMetric = (result, key, value) => {
    if (!key.startsWith('poi.') && !key.startsWith('hv_bus.'))
        return false;
    const [busKey, field] = key.split('.');
    if (!busFieldSet.has(field))
        return true;
    result.bus = getOrCreateRecord(result.bus);
    result.bus[busKey] = result.bus[busKey] ?? {};
    assignFields(result.bus[busKey], mapBusField(field), value);
    return true;
};
const assignTotalAssetMetric = (result, key, value) => {
    if (key.startsWith('total-pv.') || key.startsWith('total-as_available.')) {
        const [prefix, field] = key.split('.');
        const target = prefix === 'total-pv' ? 'pv' : 'wind';
        result[target] = (result[target] ?? {});
        result[target][mapGenField(field)] = value;
        return true;
    }
    if (key.startsWith('total-bess.')) {
        result.bess = getOrCreateRecord(result.bess);
        result.bess['bess-1'] = result.bess['bess-1'] ?? {};
        result.bess['bess-1'][key.split('.')[1]] = value;
        return true;
    }
    if (!key.startsWith('total-thermal.'))
        return false;
    result.thermal = getOrCreateRecord(result.thermal);
    result.thermal['thermal-1'] = result.thermal['thermal-1'] ?? {};
    result.thermal['thermal-1'][key.split('.')[1]] = value;
    return true;
};
const assignTopLevelV2Metric = (result, key, value) => {
    if (assignScalarMetric(result, key, value))
        return;
    if (assignOperationMetric(result, key, value))
        return;
    if (assignBusMetric(result, key, value))
        return;
    assignTotalAssetMetric(result, key, value);
};
const assignMvBusV2Metric = (result, key, value, requestKeyMaps) => {
    const parts = key.split('.');
    const requestMvBusKey = requestKeyMaps?.mvBusKeys.get(normalizeResultIdentifier(parts[0]));
    if (!requestMvBusKey)
        return false;
    const mvBusKey = requestMvBusKey;
    result.mv_bus = getOrCreateRecord(result.mv_bus);
    result.mv_bus[mvBusKey] = result.mv_bus[mvBusKey] ?? emptyMvBus();
    if (parts.length === 2 && busFieldSet.has(parts[1])) {
        assignFields(result.mv_bus[mvBusKey].bus, mapBusField(parts[1]), value);
        return true;
    }
    if (parts.length === 3 && ['bus', 'feeder'].includes(parts[1]) && busFieldSet.has(parts[2])) {
        assignFields(result.mv_bus[mvBusKey].bus, mapBusField(parts[2]), value);
        return true;
    }
    if (parts.length !== 3)
        return true;
    const requestAsset = requestKeyMaps?.assetKeys.get(`${mvBusKey}:${normalizeResultIdentifier(parts[1])}`);
    if (!requestAsset)
        return true;
    const { group, key: assetKey } = requestAsset;
    const asset = getOrCreateMvBusAsset(result, mvBusKey, group, assetKey);
    asset[group === 'bess' || group === 'thermal' ? parts[2] : mapGenField(parts[2])] = value;
    result.mv_bus[mvBusKey][group] = {
        ...(result.mv_bus[mvBusKey][group] ?? {}),
        [assetKey]: asset,
    };
    return true;
};
const cloneTsPoint = (value) => Array.isArray(value[0]) ? value.map((row) => [...row]) : [...value];
const sumTsPoints = (left, right) => {
    if (!left)
        return cloneTsPoint(right);
    if (Array.isArray(left[0]) && Array.isArray(right[0])) {
        return left.map((row, rowIndex) => row.map((value, columnIndex) => value + (right[rowIndex]?.[columnIndex] ?? 0)));
    }
    return left.map((value, index) => value + (right[index] ?? 0));
};
const addGenAssetToTotal = (total, asset) => {
    objectEntries(asset).forEach(([field, value]) => {
        if (value)
            total[field] = sumTsPoints(total[field], value);
    });
};
const collectMvBusGenTotal = (mvBuses, group) => {
    const total = {};
    Object.values(mvBuses).forEach((mvBus) => {
        Object.values(mvBus[group] ?? {}).forEach((asset) => {
            addGenAssetToTotal(total, asset);
        });
    });
    return Object.keys(total).length > 0 ? total : null;
};
const collectMvBusAssets = (mvBuses, group, prefix) => {
    const assets = {};
    let assetIndex = 0;
    Object.values(mvBuses).forEach((mvBus) => {
        Object.values(mvBus[group] ?? {}).forEach((asset) => {
            assetIndex += 1;
            assets[`${prefix}-${assetIndex}`] = asset;
        });
    });
    return assetIndex > 0 ? assets : null;
};
const mergeAssetRecords = (projected, existing) => {
    const result = { ...projected };
    objectEntries(existing ?? {}).forEach(([key, asset]) => {
        result[key] = { ...(result[key] ?? {}), ...asset };
    });
    return result;
};
const projectMvBusAssetsToDashboardShape = (result) => {
    if (!result.mv_bus)
        return result;
    const pv = collectMvBusGenTotal(result.mv_bus, 'pv');
    const wind = collectMvBusGenTotal(result.mv_bus, 'as_available');
    const bess = collectMvBusAssets(result.mv_bus, 'bess', 'bess');
    const thermal = collectMvBusAssets(result.mv_bus, 'thermal', 'thermal');
    if (pv)
        result.pv = { ...pv, ...(result.pv ?? {}) };
    if (wind)
        result.wind = { ...wind, ...(result.wind ?? {}) };
    if (bess) {
        result.bess = mergeAssetRecords(bess, result.bess);
    }
    if (thermal) {
        result.thermal = mergeAssetRecords(thermal, result.thermal);
    }
    return result;
};
const parsePlantSimTsResultsV2Entries = (entries, timestamp, options) => {
    const requestKeyMaps = buildRequestKeyMaps(options?.request);
    const result = entries.reduce((result, [key, value]) => {
        if (!assignMvBusV2Metric(result, key, value, requestKeyMaps)) {
            assignTopLevelV2Metric(result, key, value);
        }
        return result;
    }, { timestamp });
    return projectMvBusAssetsToDashboardShape(result);
};
const parsePlantSimTsResultsV2Table = (rawStr, options) => {
    const table = parseCsvString(rawStr, 1);
    return parsePlantSimTsResultsV2Entries(objectEntries(table).map(([key, vals]) => [key, parseTsCol(vals)]), table.timestamp, options);
};
const parsePlantSimTsResultsV212x24 = (rawStr, options) => {
    const trimmed = parseMatrix(rawStr).slice(1);
    const entries = _.range(0, trimmed.length, 25)
        .map((i) => [trimmed[i][2], parse12x24(trimmed.slice(i + 1, i + 25))])
        .filter(([key]) => Boolean(key));
    return parsePlantSimTsResultsV2Entries(entries, _.range(1, 13).map(String), options);
};
export const parsePlantSimTsResults = (rawStr, parse12x24s = false) => {
    if (!parse12x24s) {
        const table = parseCsvString(rawStr, 1);
        return parseResults(objectEntries(table), table.timestamp);
    }
    const matrix = rawStr.split('\n').map((row) => row.split(','));
    const trimmed = matrix.slice(1, matrix.length);
    const sectionedEntries = _.range(0, trimmed.length, 25)
        .map((i) => [trimmed[i][2], trimmed.slice(i + 1, i + 25)])
        .filter(([key]) => Boolean(key));
    return parseResults(sectionedEntries, _.range(0, 13).map(String));
};
export const parsePlantSimTsResultsV2 = (rawStr, parse12x24s = false, options) => parse12x24s
    ? parsePlantSimTsResultsV212x24(rawStr, options)
    : parsePlantSimTsResultsV2Table(rawStr, options);
export function getOutputs(annualTs, key, nested_key, agg = 'sum') {
    if (!annualTs)
        return null;
    const output = annualTs[agg][key];
    if (nested_key && !Array.isArray(output)) {
        return output && nested_key in output ? output[nested_key] : null;
    }
    return output ? (output ?? null) : null;
}
export function aggregator(vals, firstYrOnly, agg = 'sum') {
    if (!vals) {
        return 0;
    }
    if (firstYrOnly && vals.length > 0) {
        return vals[0];
    }
    return agg === 'sum' ? _.sum(vals) : _.mean(vals);
}
export function getAnnualTs(ts) {
    if (ts?.annual_for_term_sum && ts?.annual_for_term_avg) {
        return {
            sum: ts.annual_for_term_sum,
            avg: ts.annual_for_term_avg,
        };
    }
    return null;
}
//# sourceMappingURL=parse.js.map